import { IconButton } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { SelectableTable } from '../../SelectableTable/SelectableTable.component';
import { FolderMessageTableItem, MessagesFilter, MessagesSortById, MessageTableItem } from './MessagesList.types';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { compareFolderMessagesBySentDateAntichronologically, getMessageTableItem } from './utils';
import MessageCell from './MessageCell.component';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { mailboxActions } from '../../../reducers/mailboxReducer/mailboxSlice';
import {
  Conversation,
  ConversationMessage,
  ConversationMessagesResponse,
  Folder,
  FolderMessage,
  foldersWithUnReadBold,
  Message,
  SpecialCaseSolvingSuccessResults,
} from '../../../models/communication.types';
import DateCell from './DateCell.component';
import {
  closeHelpdesk,
  closeSpecialCase,
  getConversationMessages,
} from '../../../services/communication/communication.service';
import { AxiosResponse } from 'axios';
import { useLayoutContext } from '../../../Context/Layout.context';
import HelpdeskSolveIcon from '../../../assets/icons/helpdesk-solve.svg';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import { Prompt } from '../../Prompt/Prompt.component';
import { getSpecialCaseCardRedirectorUrl } from '../../../utils/routerUrls';
import { useHistory } from 'react-router-dom';
import usePermission, { UserPermission } from '../../../hooks/usePermission';
import { activeMessageBackgroundColor } from '../colors';
import { useTranslation } from 'react-i18next';
import useSessionUser from '../../../hooks/useSessionUser';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fillRed } from '../../../utils/contrastSvgAttributes';
import { SelectableTableColumn } from '../../SelectableTable/SelectableTable.types';

interface ConversationTableProps {
  message: FolderMessage;
  conversationId: string;
  conversation?: Conversation;
  open: boolean;
  filter: MessagesFilter;
  sortByColumnId: MessagesSortById | undefined;
  isDescending: boolean;
  isDefaultFilterAndSorting: boolean;
}

const ConversationTable: React.FC<ConversationTableProps> = (props) => {
  const activeFolder = useSelector((state: RootState) => state.mailbox.activeFolder);
  const activeMessageId = useSelector((state: RootState) => state.mailbox.activeMessageId);
  const loadedMessageContentMessageId = useSelector(
    (state: RootState) => state.mailbox.loadedMessageContent?.messageId,
  );
  const messageToUpdateFlag = useSelector((state: RootState) => state.mailbox.messageToUpdateFlag);
  const messagesToInsert = useSelector((state: RootState) => state.mailbox.messagesToInsert);
  const isLoadingMessageContent = useSelector((state: RootState) => state.mailbox.isLoadingMessageContent);
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { openAlert, showLoader } = useLayoutContext();

  const [headerMessage, setHeaderMessage] = useState<FolderMessage>({ ...props.message });
  const [conversationMessages, setConversationMessages] = useState<ConversationMessage[]>(
    props.conversation ? props.conversation.conversationMessages : [],
  );
  const [conversationTableItems, setConversationTableItems] = useState<MessageTableItem[]>([]);
  const [conversationOpened, setConversationOpened] = useState<boolean>(props.open);
  const [specialCaseIdWhileSolving, setSpecialCaseIdWhileSolving] = useState<string | null>(null);
  const [specialCaseSolvingSuccessResults, setSpecialCaseSolvingSuccessResults] =
    useState<SpecialCaseSolvingSuccessResults | null>(null);
  const permissionToSolveHelpDesk = usePermission(UserPermission.AdminOkeHelpdeskProcessing);
  const [isHelpdeskCloseConfirmation, setIsHelpdeskCloseConfirmation] = useState<boolean>(false);
  const { user } = useSessionUser();

  const messagesColumns: SelectableTableColumn[] = [
    {
      id: 'flag',
      type: 'actions',
      title: t('messagebox:messages.table.flag'),
      filterType: 'filter',
      width: '7%',
      sortable: false,
      skipEnterMethod: true,
    },
    {
      id: 'participant',
      type: 'text',
      title: t('messagebox:messages.table.participant'),
      filterType: 'search',
      sortable: false,
      width: '30%',
    },
    {
      id: 'subject',
      type: 'text',
      title: t('messagebox:messages.table.subject'),
      filterType: 'search',
      sortable: true,
      width: '30%',
    },
    {
      id: 'assessmentSession',
      type: 'text',
      title: t('messagebox:messages.table.assessmentSession'),
      filterType: 'search',
      sortable: true,
      width: '20%',
    },
    {
      id: 'monit',
      type: 'text',
      title: t('messagebox:messages.table.monit'),
      filterType: 'filter',
      sortable: false,
      width: '40px',
    },
    {
      id: 'sendDate',
      type: 'text',
      title: t('messagebox:messages.table.sendDate'),
      filterType: 'filter',
      sortable: true,
      width: '13%',
    },
  ];

  useEffect(() => {
    if (props.conversation === undefined && conversationOpened) {
      showLoader(true);
      getConversationMessages(
        activeFolder,
        props.conversationId,
        props.filter,
        props.sortByColumnId,
        props.isDescending,
        0, //todo: temporary
        100, //todo: temporary
      )
        .then(
          (response: AxiosResponse<ConversationMessagesResponse>) => {
            setConversationMessages(response.data.conversationMessages);
          },
          () => {
            openAlert('get_error');
          },
        )
        .finally(() => {
          dispatch(mailboxActions.setMessageToUpdateFlag(null));
          showLoader(false);
        });
    }
  }, [
    props.conversationId,
    props.conversation,
    conversationOpened,
    activeFolder,
    props.filter,
    props.sortByColumnId,
    props.isDescending,
  ]);

  useEffect(() => {
    setConversationOpened(props.open);
  }, [props.open]);

  const openSolvingSpecialCaseConfirmation = (specialCaseId: string) => {
    setSpecialCaseIdWhileSolving(specialCaseId);
  };

  const handleSpecialCaseSolving = () => {
    closeSpecialCase(props.conversationId)
      .then(
        (response) => {
          setSpecialCaseSolvingSuccessResults(response.data);
        },
        () => openAlert('send_error'),
      )
      .finally(() => setSpecialCaseIdWhileSolving(null));
  };

  const handleHelpdeskSolving = () => {
    closeHelpdesk(props.conversationId)
      .then(
        () => {
          dispatch(mailboxActions.addConversationToMove(props.conversationId));
          openAlert('success');
        },
        () => openAlert('send_error'),
      )
      .finally(() => setIsHelpdeskCloseConfirmation(false));
  };

  const setMonitIcon = () => {
    if (activeFolder === Folder.SpecialCasesToSolve && props.message.isCurrentUserConversationInitiator) {
      return (
        <TooltipOne title="Zamknij Szczególny przypadek" placement="top">
          <IconButton
            disableRipple
            style={{ width: '100%', height: '100%' }}
            onClick={() => openSolvingSpecialCaseConfirmation(props.message.assessmentSession)}
          >
            <div style={{ height: '24px', position: 'relative' }}>
              <ErrorOutlineIcon style={{ color: fillRed }} />
              <CheckCircleIcon
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '13px',
                  height: '16px',
                  width: 'auto',
                  color: fillRed,
                }}
              />
            </div>
          </IconButton>
        </TooltipOne>
      );
    }
    if (
      activeFolder === Folder.HelpdeskReported &&
      !props.message.isCurrentUserConversationInitiator &&
      permissionToSolveHelpDesk
    ) {
      return (
        <TooltipOne title="Rozwiąż zgłoszenie" placement="top">
          <IconButton
            disableRipple
            style={{ width: '30px', height: '30px' }}
            onClick={() => setIsHelpdeskCloseConfirmation(true)}
          >
            <img src={HelpdeskSolveIcon} alt="Ikona do rozwiązania zgłoszenia helpdesk" />
          </IconButton>
        </TooltipOne>
      );
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (conversationMessages.length === 0 && props.conversation !== undefined) {
      dispatch(mailboxActions.addConversationToMove(props.conversationId));
      return;
    }

    const isRead = !foldersWithUnReadBold.includes(activeFolder) || headerMessage.unreadMessages === 0;
    const tableData: MessageTableItem[] = [
      {
        id: headerMessage.messageId + '_header',
        flag: null,
        flagId: null,
        participant: {
          colSpan: 3,
          data: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <IconButton
                disableRipple
                aria-label="expand row"
                size="small"
                onClick={() => setConversationOpened((prevState) => !prevState)}
                style={{ marginLeft: '-10px' }}
              >
                {conversationOpened ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
              <MessageCell isRead={isRead} withEllipsis={true} isTooltip={true}>
                {headerMessage.subject}
              </MessageCell>
            </div>
          ),
        },
        subject: null,
        assessmentSession: null,
        monit: setMonitIcon(),
        sendDate: <DateCell isRead={isRead} message={headerMessage} smallerFont={true} />,
        stickyRowHeight: '40px',
      },
    ];

    const conversationItems: MessageTableItem[] = conversationOpened
      ? conversationMessages.map((message: ConversationMessage) => {
          dispatch(mailboxActions.setMessageToUpdateFlag(null));
          return getMessageTableItem(message, true, foldersWithUnReadBold.includes(activeFolder));
        })
      : [];

    setConversationTableItems(tableData.concat(conversationItems));
  }, [conversationOpened, conversationMessages, headerMessage]);

  const activateConversationRow = useCallback(
    (row: FolderMessageTableItem) => {
      if (isLoadingMessageContent) return;
      const message = conversationMessages.find((item: Message) => item.messageId === row.id);
      if (message) {
        dispatch(mailboxActions.activateMessage(message.messageId));
      }
    },
    [conversationMessages, isLoadingMessageContent],
  );

  useEffect(() => {
    setConversationMessages((prevState) => {
      let readStatusChanged = false;
      const newState = prevState.map((item) => {
        if ((item.messageId === activeMessageId || item.messageId === loadedMessageContentMessageId) && !item.isRead) {
          readStatusChanged = true;
          return { ...item, isRead: true };
        } else {
          return item;
        }
      });
      if (readStatusChanged) {
        setHeaderMessage((prevState) => {
          return { ...prevState, unreadMessages: prevState.unreadMessages - 1 };
        });
        dispatch(mailboxActions.decreaseCurrentFolderUnreadMessages(user.profile.sub));
      }
      return newState;
    });
  }, [activeMessageId, loadedMessageContentMessageId]);

  useEffect(() => {
    if (props.conversation?.conversationMessages) {
      setConversationMessages(props.conversation?.conversationMessages);
    }
  }, [props.conversation?.conversationMessages]);

  useEffect(() => {
    setHeaderMessage({ ...props.message });
  }, [props.message]);

  useEffect(() => {
    if (messageToUpdateFlag) {
      setConversationMessages((prevState) => {
        const newState = prevState.map((item) => {
          if (messageToUpdateFlag.messageId === item.messageId) {
            return { ...item, flag: messageToUpdateFlag.flag };
          } else {
            return item;
          }
        });
        return newState;
      });
      dispatch(mailboxActions.setMessageToUpdateFlag(null));
    }
  }, [messageToUpdateFlag]);

  useEffect(() => {
    if (messagesToInsert.length) {
      if (props.isDefaultFilterAndSorting) {
        setConversationMessages((prevState) => {
          const messagesToInsertToConversationMessages: Message[] = [];
          messagesToInsert.forEach((messageToInsert) => {
            if (messageToInsert.message.conversationId === headerMessage.conversationId) {
              messagesToInsertToConversationMessages.push(messageToInsert.message);
            }
          });
          let newState: ConversationMessage[] = [];
          newState = [
            ...prevState,
            ...messagesToInsertToConversationMessages.map((item) => {
              return {
                ...item,
                isRead: false,
                assessmentSession: headerMessage.assessmentSession || item.assessmentSession,
              };
            }),
          ];
          newState.sort(compareFolderMessagesBySentDateAntichronologically);
          return newState;
        });
      }
    }
  }, [messagesToInsert, props.isDefaultFilterAndSorting]);

  return (
    <>
      <SelectableTable<MessageTableItem>
        columns={messagesColumns}
        rows={conversationTableItems}
        closeFilterItem={undefined}
        withHeaderHidden={true}
        withoutFilterRow={true}
        rowBackgroundColor={'rgba(240, 240, 245)'}
        activeRowBackgroundColor={activeMessageBackgroundColor}
        activeRowId={activeMessageId || undefined}
        cellsBorderBottom={'none'}
        withColumnWidths={true}
        isMultiChoice={false}
        onRowClick={activateConversationRow}
        caption="Skrzynka odbiorcza"
      />
      {specialCaseIdWhileSolving && (
        <Prompt
          type="confirmation"
          mainText="Czy na pewno chcesz zamknąć szczególny przypadek?"
          mainActionButtonText="TAK"
          onConfirm={handleSpecialCaseSolving}
          onCancel={() => setSpecialCaseIdWhileSolving(null)}
        />
      )}

      {specialCaseSolvingSuccessResults !== null && (
        <Prompt
          type="success"
          mainText={
            <>
              <div>Zamknięcie szczególnego przypadku zakończone sukcesem.</div>
              <div>Możesz ponownie oceniać pracę.</div>
            </>
          }
          mainActionButtonText="Przejdź do wykazu ocenianych kart"
          onConfirm={() => {
            history.push(
              getSpecialCaseCardRedirectorUrl(
                specialCaseSolvingSuccessResults.examTaskGroupSolutionScoreId,
                '0',
                specialCaseSolvingSuccessResults.assessmentSessionId,
              ),
            );
            setSpecialCaseSolvingSuccessResults(null);
            dispatch(mailboxActions.addConversationToMove(props.conversationId));
          }}
          cancelButtonText="Zamknij"
          onCancel={() => {
            setSpecialCaseSolvingSuccessResults(null);
            dispatch(mailboxActions.addConversationToMove(props.conversationId));
          }}
        />
      )}
      {isHelpdeskCloseConfirmation && (
        <Prompt
          type="confirmation"
          mainText="Czy na pewno chcesz rozwiązać zgłoszenie?"
          mainActionButtonText="TAK"
          onConfirm={handleHelpdeskSolving}
          onCancel={() => setIsHelpdeskCloseConfirmation(false)}
        />
      )}
    </>
  );
};

export default ConversationTable;
